import type {
  InstrumentApiResponse,
  ReferenceDataField,
} from '@moment/trading-types/instrument-data'
import type {
  LiveInventoryPostResponse,
  LiveInventoryReturnValues,
} from '@/types/api/live-inventory'

import { BondType } from '@moment/trading-types/reference-data'

import { formatMoney, PLACEHOLDER } from '@/utils/presentation/formatting'
import { referenceDataTransformationConfig } from '@/utils/presentation/instrument-data-transforms'

interface SummaryItem {
  label: string
  value: string | React.ReactNode
}

// Types for reference data organization
interface ReferenceDataFields {
  issueDetails: Array<ReferenceDataField>
  redemptionDetails: Array<ReferenceDataField>
  countryInformation: Array<ReferenceDataField>
  datesAndEligibility: Array<ReferenceDataField>
  moodysRatings: Array<ReferenceDataField>
  spRatings: Array<ReferenceDataField>
  pricingAndAnalytics: Array<ReferenceDataField>
}

// Organize reference data fields by category
export const referenceDataFields: ReferenceDataFields = {
  issueDetails: [
    'isin',
    'issuer',
    'type',
    'sector',
    'purpose',
    'issue_date',
    'original_issue_date',
    'issue_price',
    'issue_size',
    'ticker',
  ],

  redemptionDetails: [
    'maturity_date',
    'par_value',
    'sinking_fund',
    'callable',
    'call_type',
    'next_call_date',
    'next_call_price',
    'puttable',
    'convertible',
    'perpetual',
  ],

  countryInformation: [
    'currency',
    'country_issue',
    'country_domicile',
    'municipal_state',
    'municipal_taxable_federal',
  ],

  datesAndEligibility: [
    'dated_date',
    'issue_date',
    'original_issue_date',
    'maturity_date',
    'first_coupon_date',
    'last_coupon_date',
    'next_coupon_date',
  ],

  moodysRatings: ['moodys_rating', 'moodys_rating_date'],

  spRatings: [
    'sp_rating',
    'sp_rating_date',
    'sp_creditwatch',
    'sp_creditwatch_date',
    'sp_outlook',
    'sp_outlook_date',
  ],

  pricingAndAnalytics: [
    'close_price',
    'close_price_asof',
    'close_ytm',
    'close_ytw',
    'duration',
    'convexity',
    'treasury_benchmark',
    'treasury_subtype',
  ],
}

// helper to format field value
export function formatFieldValue<T extends ReferenceDataField>(
  field: T,
  value: LiveInventoryPostResponse['data'][0][keyof LiveInventoryPostResponse['data'][0]],
): string {
  const transformer = referenceDataTransformationConfig[field]
  if (transformer) {
    return transformer.valueTransformation(value as InstrumentApiResponse[T])
  }
  return value?.toString() ?? PLACEHOLDER
}

// helper to get field label
export function getFieldLabel(
  field: keyof typeof referenceDataTransformationConfig,
): string {
  const transformer = referenceDataTransformationConfig[field]
  if (transformer) {
    return transformer.keyTransformation(field)
  }
  return field
}

// helper to get all required fields
export function getAllReferenceDataFields(): LiveInventoryReturnValues[] {
  return Object.values(
    referenceDataFields,
  ).flat() as LiveInventoryReturnValues[]
}

export function getBondSummaryItems(
  data: LiveInventoryPostResponse['data'][0] | null,
): SummaryItem[] {
  if (!data) return []

  const commonItems: SummaryItem[] = [
    {
      label: 'Par value',
      value: formatMoney(data.par_value, { numDigits: 0 }),
    },
    {
      label: 'Next call date',
      value: data.next_call_date ?? PLACEHOLDER,
    },
    {
      label: 'Next call price',
      value: formatMoney(data.next_call_price),
    },
  ]

  const bondType = data.type as BondType

  const specificItem: SummaryItem = {
    label:
      bondType === BondType.Treasury
        ? 'Subtype'
        : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
          bondType === BondType.Municipal
          ? 'Use of proceeds'
          : 'Sector',
    value:
      bondType === BondType.Treasury
        ? (data.treasury_subtype ?? PLACEHOLDER)
        : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
          bondType === BondType.Municipal
          ? (data.purpose ?? PLACEHOLDER)
          : (data.sector ?? PLACEHOLDER),
  }

  return [...commonItems, specificItem]
}
